import { ROW_BLOCKS } from '../config/keyboard-layouts';
import type { RowBlock } from '../config/keyboard-layouts';
import { clamp, fromAbsIndex, isWhite } from '../config/music';
import type { Settings } from '../state/settings';
import { whitesOf } from './rows';

/** Total white keys the two blocks cover when laid end to end (≈ 20 ≈ 3 oct). */
const WINDOW_WHITES = ROW_BLOCKS.reduce((n, b) => n + b.whites.length, 0);

export interface KeyBindings {
  /** physical key character → absolute note index */
  readonly byKey: ReadonlyMap<string, number>;
  /** absolute note index → physical key character (for the on-screen labels) */
  readonly byNote: ReadonlyMap<number, string>;
  /** Lowest/highest note the computer keyboard can currently reach, or null. */
  readonly lowAbs: number | null;
  readonly highAbs: number | null;
  /** How many of the visible notes are reachable. */
  readonly covered: number;
  readonly total: number;
}

const EMPTY: KeyBindings = {
  byKey: new Map(),
  byNote: new Map(),
  lowAbs: null,
  highAbs: null,
  covered: 0,
  total: 0,
};

/**
 * The mapping is one contiguous block of white keys (the two physical halves
 * laid end to end), placed as a sliding window over the range's white notes.
 * It is independent of how many display rows there are, so it always reads as
 * one unbroken zone. rowShift is a signed offset from the centred position.
 */
function windowStart(s: Settings): { start: number; room: number } {
  const whiteCount = whitesOf(rangeNotes(s)).length;
  const room = Math.max(0, whiteCount - WINDOW_WHITES);
  const centre = Math.floor(room / 2);
  return { start: clamp(centre + s.rowShift, 0, room), room };
}

/** How far the window can still slide, so the shift can be bounded. */
export function rowShiftBounds(s: Settings): { min: number; max: number } {
  const { room } = windowStart(s);
  const centre = Math.floor(room / 2);
  return { min: -centre, max: room - centre };
}

function rangeNotes(s: Settings): number[] {
  const out: number[] = [];
  for (let abs = s.startAbs; abs <= s.endAbs; abs++) out.push(abs);
  return out;
}

function bindBlock(
  block: RowBlock,
  row: readonly number[],
  offset: number,
  byKey: Map<string, number>,
  byNote: Map<number, string>,
): void {
  const whites = whitesOf(row);
  const capacity = block.whites.length;
  const start = clamp(offset, 0, Math.max(0, whites.length - capacity));
  const windowed = whites.slice(start, start + capacity);
  if (windowed.length === 0) return;

  const set = (ch: string | undefined, abs: number): void => {
    if (!ch || byKey.has(ch) || byNote.has(abs)) return;
    byKey.set(ch, abs);
    byNote.set(abs, ch);
  };

  windowed.forEach((abs, i) => set(block.whites[i], abs));

  // A black key exists between two whites only when they are two semitones
  // apart; at E→F and B→C the position stays deliberately unbound, which is
  // exactly where the physical keyboard reads as a gap.
  for (let i = 0; i < windowed.length - 1; i++) {
    const here = windowed[i];
    const next = windowed[i + 1];
    if (here === undefined || next === undefined) continue;
    if (next - here === 2) set(block.blacks[i], here + 1);
  }

  const first = windowed[0];
  if (first !== undefined) {
    const below = first - 1;
    if (below >= row[0]! && !isWhite(fromAbsIndex(below).noteIndex)) set(block.blackBefore, below);
  }

  // A row can end on a black key, which has no white to its right to sit
  // between; the next key along the black row covers it.
  const last = windowed[windowed.length - 1];
  const rowEnd = row[row.length - 1];
  if (last !== undefined && rowEnd !== undefined) {
    const above = last + 1;
    if (above <= rowEnd && !isWhite(fromAbsIndex(above).noteIndex)) {
      set(block.blacks[windowed.length - 1], above);
    }
  }
}

export function computeKeyBindings(s: Settings): KeyBindings {
  const byKey = new Map<string, number>();
  const byNote = new Map<number, string>();
  const total = s.endAbs - s.startAbs + 1;

  // One contiguous window over the whole range: the lower physical block
  // (z-row + home row) then the upper block (top + number rows), laid end to
  // end. The blocks always abut, so the mapping is a single unbroken zone
  // regardless of how the display splits the keyboard into rows.
  const notes = rangeNotes(s);
  const { start } = windowStart(s);
  let base = 0;
  for (const block of ROW_BLOCKS) {
    bindBlock(block, notes, start + base, byKey, byNote);
    base += block.whites.length;
  }

  if (byNote.size === 0) return { ...EMPTY, total };
  const sorted = [...byNote.keys()].sort((a, b) => a - b);
  return {
    byKey,
    byNote,
    lowAbs: sorted[0] ?? null,
    highAbs: sorted[sorted.length - 1] ?? null,
    covered: byNote.size,
    total,
  };
}
